import { useEffect, useMemo, useState } from "react";
import {
  generarCuotasMes, getConfig, listAlumnos, listApoderados, listCuotas, saveCuota,
} from "../../lib/data";
import { formatCLP, formatFecha } from "../../lib/format";
import { MESES_ES, type ConfigGeneral, type Cuota, type EstadoCuota, type MetodoPago } from "../../lib/types";
import { EmptyState, EstadoBadge, Modal, Spinner, StatCard } from "../../components/ui";

const HOY = new Date();

export function Cuotas() {
  const [ano, setAno] = useState(HOY.getFullYear());
  const [mes, setMes] = useState(HOY.getMonth() + 1);
  const [cfg, setCfg] = useState<ConfigGeneral | null>(null);
  const [cuotas, setCuotas] = useState<Cuota[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pago, setPago] = useState<Cuota | null>(null);
  const [metodo, setMetodo] = useState<MetodoPago>("transferencia");
  const [comprobante, setComprobante] = useState("");
  const [filtro, setFiltro] = useState<EstadoCuota | "todos">("todos");

  async function reload() {
    setLoading(true);
    try {
      const [c, config] = await Promise.all([listCuotas(ano), getConfig()]);
      setCfg(config);
      setCuotas(c);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [ano]);

  const delMes = useMemo(
    () => cuotas
      .filter((c) => c.mes === mes && (filtro === "todos" || c.estado === filtro))
      .sort((a, b) => a.alumnoNombre.localeCompare(b.alumnoNombre)),
    [cuotas, mes, filtro],
  );

  const resumen = useMemo(() => {
    const m = cuotas.filter((c) => c.mes === mes);
    const recaudado = m.filter((c) => c.estado === "pagado").reduce((s, c) => s + c.montoFinal, 0);
    const porCobrar = m.filter((c) => ["pendiente", "por_vencer", "abono"].includes(c.estado)).reduce((s, c) => s + c.montoFinal, 0);
    return { total: m.length, recaudado, porCobrar };
  }, [cuotas, mes]);

  async function generar() {
    if (!cfg) return;
    setBusy(true); setError("");
    try {
      const [alumnos, apoderados] = await Promise.all([listAlumnos(), listApoderados()]);
      const creadas = await generarCuotasMes(mes, ano, cfg, alumnos, apoderados, cuotas);
      await reload();
      if (creadas === 0) setError("No había cuotas nuevas por generar para este mes.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function registrarPago() {
    if (!pago) return;
    try {
      await saveCuota({
        ...pago,
        estado: "pagado",
        fechaPago: new Date().toISOString(),
        metodoPago: metodo,
        comprobante: comprobante || null,
      });
      setPago(null); setComprobante("");
      reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">Cuotas y pagos</h1>
          <p className="text-sm text-slate-500">Genera cuotas del mes y registra pagos</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select className="input max-w-[8rem]" value={mes} onChange={(e) => setMes(Number(e.target.value))}>
            {MESES_ES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select className="input max-w-[6rem]" value={ano} onChange={(e) => setAno(Number(e.target.value))}>
            {[HOY.getFullYear() - 1, HOY.getFullYear(), HOY.getFullYear() + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn-primary" onClick={generar} disabled={busy || !cfg}>
            {busy ? "Generando…" : "⚡ Generar cuotas del mes"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <StatCard label={`Cuotas de ${MESES_ES[mes - 1]}`} value={String(resumen.total)} tone="slate" />
        <StatCard label="Recaudado" value={formatCLP(resumen.recaudado)} tone="emerald" />
        <StatCard label="Por cobrar" value={formatCLP(resumen.porCobrar)} tone="amber" />
      </div>

      <div className="flex flex-wrap gap-2">
        {(["todos", "pendiente", "por_vencer", "pagado", "abono", "beca", "exento"] as const).map((f) => (
          <button key={f} onClick={() => setFiltro(f)}
            className={`badge cursor-pointer ${filtro === f ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600"}`}>
            {f === "todos" ? "Todas" : f}
          </button>
        ))}
      </div>

      {loading ? (
        <Spinner />
      ) : delMes.length === 0 ? (
        <EmptyState title={`Sin cuotas en ${MESES_ES[mes - 1]}`} hint="Usa ⚡ Generar cuotas del mes para crearlas a partir de los alumnos activos." />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                <th className="th">Alumno</th><th className="th">Práctica</th><th className="th">Vence</th>
                <th className="th">Base</th><th className="th">Desc.</th><th className="th">Mora</th>
                <th className="th">Total</th><th className="th">Estado</th><th className="th"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {delMes.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="td font-medium">{c.alumnoNombre}</td>
                  <td className="td capitalize">{c.practica === "volleyball" ? "Vóleibol" : "Fútbol"}</td>
                  <td className="td">{formatFecha(c.fechaVencimiento)}</td>
                  <td className="td">{formatCLP(c.montoBase)}</td>
                  <td className="td text-emerald-600">{c.descuento ? `-${formatCLP(c.descuento)}` : "—"}</td>
                  <td className="td text-rose-600">{c.recargo ? `+${formatCLP(c.recargo)}` : "—"}</td>
                  <td className="td font-semibold">{formatCLP(c.montoFinal)}</td>
                  <td className="td"><EstadoBadge estado={c.estado} /></td>
                  <td className="td text-right">
                    {c.estado !== "pagado" && c.estado !== "exento" && (
                      <button className="btn-primary !px-2 !py-1" onClick={() => { setPago(c); setMetodo("transferencia"); setComprobante(c.comprobante || ""); }}>
                        Registrar pago
                      </button>
                    )}
                    {c.fechaPago && <span className="text-xs text-slate-400">{formatFecha(c.fechaPago)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && <p className="text-sm text-amber-600">{error}</p>}

      <Modal open={!!pago} onClose={() => setPago(null)} title="Registrar pago">
        {pago && (
          <div className="space-y-3">
            <div className="rounded-xl bg-slate-50 p-3 text-sm">
              <p className="font-semibold">{pago.alumnoNombre}</p>
              <p className="text-slate-500">{MESES_ES[pago.mes - 1]} · {pago.practica === "volleyball" ? "Vóleibol" : "Fútbol"}</p>
              <p className="mt-1 text-lg font-bold text-brand-700">{formatCLP(pago.montoFinal)}</p>
              <p className="text-xs text-slate-400">{pago.cuenta}</p>
            </div>
            <div>
              <label className="label">Método de pago</label>
              <select className="input" value={metodo} onChange={(e) => setMetodo(e.target.value as MetodoPago)}>
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            <div>
              <label className="label">N° comprobante / referencia</label>
              <input className="input" value={comprobante} onChange={(e) => setComprobante(e.target.value)} placeholder="Opcional" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-ghost" onClick={() => setPago(null)}>Cancelar</button>
              <button className="btn-primary" onClick={registrarPago}>Confirmar pago</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
